import React, { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Alert, AlertDescription } from '../ui/alert'
import { Checkbox } from '../ui/checkbox'
import {
  Loader2,
  Monitor,
  HardDrive,
  Video,
  CheckCircle2,
  AlertCircle,
  Laptop,
  Cable,
  FileVideo,
  Cloud,
  Settings
} from 'lucide-react'
import { companyAPI } from '../../services/api'
import { toast } from '../../hooks/use-toast'

export default function TechnicalSetupModal({ open, onClose }) {
  const [loading, setLoading] = useState(false)
  const [savingItem, setSavingItem] = useState(null)
  const [checklistStatus, setChecklistStatus] = useState({})
  const [allCompleted, setAllCompleted] = useState(false)

  const checklistItems = [
    {
      id: 'laptop',
      title: 'Laptop and Power Adapter',
      description: 'Confirm laptop is working and power adapter is compatible with venue outlets',
      icon: Laptop,
      tips: [
        'Check battery health - should hold charge for at least 2 hours',
        'Test power adapter with Indian standard outlets',
        'Consider bringing a universal adapter as backup'
      ]
    },
    {
      id: 'adapters',
      title: 'HDMI/Display Adapters',
      description: 'Test all display adapters and ensure compatibility with presentation screens',
      icon: Cable,
      tips: [
        'Bring HDMI, USB-C, and DisplayPort adapters',
        'Test adapters with external monitors beforehand',
        'Pack extra cables - they often fail at critical moments'
      ]
    },
    {
      id: 'video',
      title: 'Record Presentation Video',
      description: 'Record your demo as a video file (no live coding allowed during the event)',
      icon: FileVideo,
      tips: [
        'Record in 1080p resolution for best quality',
        'Keep video under 10 minutes for your demo slot',
        'Include voice narration for better engagement',
        'Save in MP4 format for universal compatibility'
      ]
    },
    {
      id: 'backup',
      title: 'Backup Files',
      description: 'Create multiple backups of presentation and demo video',
      icon: Cloud,
      tips: [
        'Save on at least 2 USB drives',
        'Upload to Google Drive or Dropbox',
        'Email yourself a copy as last resort',
        'Export presentation as PDF for emergency use'
      ]
    },
    {
      id: 'test',
      title: 'Test Complete Setup',
      description: 'Do a full test run with all equipment connected',
      icon: Settings,
      tips: [
        'Practice switching between slides and video',
        'Test audio output if video has sound',
        'Time your presentation to stay within limits',
        'Have a colleague review your setup'
      ]
    }
  ]

  // Load saved checklist status
  useEffect(() => {
    if (open) {
      loadChecklistStatus()
    }
  }, [open])

  // Check if all items are completed
  useEffect(() => {
    const completed = checklistItems.every(item => checklistStatus[item.id] === true)
    setAllCompleted(completed)
  }, [checklistStatus])

  const loadChecklistStatus = async () => {
    setLoading(true)
    try {
      const response = await companyAPI.getTechnicalChecklist()
      if (response.data.success && response.data.checklist) {
        // Convert backend format to frontend format
        const status = {}
        Object.keys(response.data.checklist).forEach(key => {
          status[key] = response.data.checklist[key]
        })
        setChecklistStatus(status)
      }
    } catch (error) {
      // If no checklist exists yet, that's okay - start with empty
      console.log('No existing checklist found')
    } finally {
      setLoading(false)
    }
  }

  const handleCheckChange = async (itemId, checked) => {
    // Update UI immediately
    const newStatus = {
      ...checklistStatus,
      [itemId]: checked
    }
    setChecklistStatus(newStatus)

    // Save to backend
    setSavingItem(itemId)
    try {
      await companyAPI.updateTechnicalChecklistItem(itemId, checked)

      // Show toast for completion
      if (checked) {
        const item = checklistItems.find(i => i.id === itemId)
        toast.success(`${item.title} marked as complete!`)
      }

      // Check if all items are now completed
      const allChecked = checklistItems.every(item =>
        item.id === itemId ? checked : newStatus[item.id] === true
      )

      if (allChecked) {
        // Auto-submit when all items are checked
        toast.success('All technical setup requirements completed! Well done!')
        setTimeout(() => {
          onClose()
        }, 1500)
      }
    } catch (error) {
      console.error('Error updating checklist:', error)
      toast.error('Failed to save checklist item')
      // Revert on error
      setChecklistStatus({
        ...checklistStatus,
        [itemId]: !checked
      })
    } finally {
      setSavingItem(null)
    }
  }

  const handleMarkAllComplete = async () => {
    setLoading(true)
    try {
      // Update all items to completed
      const allCompleted = {}
      checklistItems.forEach(item => {
        allCompleted[item.id] = true
      })
      setChecklistStatus(allCompleted)

      // Save to backend
      await companyAPI.updateAllTechnicalChecklistItems(true)

      toast.success('All technical setup requirements marked as complete!')
      setTimeout(() => onClose(), 1500)
    } catch (error) {
      console.error('Error marking all complete:', error)
      toast.error('Failed to update checklist')
    } finally {
      setLoading(false)
    }
  }

  const getCompletedCount = () => {
    return checklistItems.filter(item => checklistStatus[item.id] === true).length
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] !flex !flex-col !overflow-hidden !p-0 [&>button]:top-6 [&>button]:right-6">
        <DialogHeader className="flex-shrink-0 p-6 pb-0 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Monitor className="h-5 w-5" />
            Technical Setup & Equipment Check
          </DialogTitle>
          <DialogDescription className="pb-4">
            Ensure all equipment and materials are ready for your presentation
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 p-6">
          {/* Progress */}
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Progress</span>
              <span className="text-sm text-muted-foreground">
                {getCompletedCount()} of {checklistItems.length} completed
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all"
                style={{ width: `${(getCompletedCount() / checklistItems.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Important Notice */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Important:</strong> A live demo is not suggested during presentations.
              Please prepare a recorded demo video to showcase your product.
            </AlertDescription>
          </Alert>

          {/* Checklist Items */}
          <div className="space-y-4">
            {checklistItems.map((item) => {
              const Icon = item.icon
              const isCompleted = checklistStatus[item.id] === true

              return (
                <Card key={item.id} className={isCompleted ? 'border-green-500' : ''}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id={item.id}
                        checked={isCompleted}
                        onCheckedChange={(checked) => handleCheckChange(item.id, checked)}
                        disabled={savingItem === item.id}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Icon className={`h-4 w-4 ${isCompleted ? 'text-green-600' : 'text-muted-foreground'}`} />
                          <label
                            htmlFor={item.id}
                            className={`font-medium cursor-pointer ${isCompleted ? 'line-through text-muted-foreground' : ''}`}
                          >
                            {item.title}
                          </label>
                          {isCompleted && (
                            <CheckCircle2 className="h-4 w-4 text-green-600 ml-auto" />
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                  {!isCompleted && (
                    <CardContent className="pt-0">
                      <div className="ml-7 space-y-1">
                        <p className="text-xs font-medium text-muted-foreground mb-2">Tips:</p>
                        <ul className="text-xs text-muted-foreground space-y-1">
                          {item.tips.map((tip, index) => (
                            <li key={index} className="flex items-start gap-1">
                              <span className="text-primary mt-0.5">•</span>
                              <span>{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  )}
                </Card>
              )
            })}
          </div>

          {/* Completion Status */}
          {allCompleted && (
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Excellent! You've completed all technical setup requirements.
                You're ready for your presentation!
              </AlertDescription>
            </Alert>
          )}
        </div>

        {/* Footer with Actions */}
        <div className="flex-shrink-0 border-t p-6 space-y-3 bg-background shadow-[0_-4px_6px_-1px_rgb(0_0_0_/_0.05)]">
          <div className="flex gap-3">
            {!allCompleted && (
              <Button
                onClick={handleMarkAllComplete}
                className="flex-1"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    Mark All Complete
                  </>
                )}
              </Button>
            )}

            <Button
              variant={allCompleted ? "default" : "outline"}
              onClick={onClose}
              className={allCompleted ? "flex-1" : ""}
            >
              {allCompleted ? 'Done' : 'Close'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}